import { useQuery } from '@tanstack/react-query';
import axiosClient from '../clients/axiosClient';
import type {
  PortfolioSeriesResponse,
  StockBarsResponse,
  StockChartRange,
  StockProfileResponse,
  StockTransactionsResponse,
} from '@shared/types';
import { retailAccountKeys } from './queryKeys';

/** Per-stock price bars for the positions-view chart (NOV-38). */
export function useStockBars(
  accountId: string | null,
  symbol: string | null,
  range: StockChartRange,
  enabled = true,
) {
  return useQuery({
    queryKey: retailAccountKeys.bars(accountId ?? '', symbol ?? '', range),
    queryFn: async () => {
      const { data } = await axiosClient.get<StockBarsResponse>(
        `/api/user-retail-accounts/${accountId}/bars/${symbol}`,
        { params: { range } },
      );
      return data;
    },
    enabled: !!accountId && !!symbol && enabled,
    staleTime: 60_000,
  });
}

/** NovaQuantis portfolio equity series for overlaying its return on the chart. */
export function usePortfolioSeries(
  accountId: string | null,
  range: StockChartRange,
  tradingStartedAt?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: retailAccountKeys.series(accountId ?? '', range),
    queryFn: async () => {
      const params: Record<string, string> = { range };
      if (tradingStartedAt) params.tradingStartedAt = tradingStartedAt;
      const { data } = await axiosClient.get<PortfolioSeriesResponse>(
        `/api/user-retail-accounts/${accountId}/portfolio-series`,
        { params },
      );
      return data;
    },
    enabled: !!accountId && enabled,
    staleTime: 60_000,
  });
}

/** Per-stock transaction (fill) history for the holdings/transactions tables. */
export function usePositionTransactions(
  accountId: string | null,
  symbol: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: retailAccountKeys.transactions(accountId ?? '', symbol ?? ''),
    queryFn: async () => {
      const { data } = await axiosClient.get<StockTransactionsResponse>(
        `/api/user-retail-accounts/${accountId}/transactions/${symbol}`,
      );
      return data;
    },
    enabled: !!accountId && !!symbol && enabled,
    staleTime: 60_000,
  });
}

/** Company details (name/exchange/class + description/industry/website). */
export function useStockProfile(
  accountId: string | null,
  symbol: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: retailAccountKeys.profile(accountId ?? '', symbol ?? ''),
    queryFn: async () => {
      const { data } = await axiosClient.get<StockProfileResponse>(
        `/api/user-retail-accounts/${accountId}/profile/${symbol}`,
      );
      return data;
    },
    enabled: !!accountId && !!symbol && enabled,
    staleTime: 12 * 60 * 60 * 1000,
  });
}
